import React, { useCallback, useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useNavigate } from "react-router";
import { useMediaQuery } from "@mui/material";
import BdrMenu from "../../../../components/BdrMenu/BdrMenu";
import BdrModal from "../../../../components/BdrModal/BdrModal";
import ClientSearchInput, {
  buildEmailSecondary,
  pickMatchedPhone,
} from "../ClientSearchInput/ClientSearchInput";
import ClientFormModal from "../ClientForm/ClientFormModal";
import { GET_RECENT_CLIENTS } from "../../../../utils/GraphQL/queries";
import "./clientsNavMenu.css";

/**
 * ClientsNavMenu — the Clients PAGE, as a navbar dropdown (and a modal on a
 * phone).
 *
 * The page it replaces held exactly three things: a search field, a "New
 * Client" button and a recents list. That is a LAUNCHER, not a destination —
 * nothing was ever read there, only used to get somewhere else — and a launcher
 * is better as a menu because you reach it without losing the page you are on.
 * `/bdr-office/clients` is gone; the owner accepted the broken bookmark.
 *
 * ─────────────────────────────────────────────────────────────────
 * TWO SHAPES, AND WHY.
 *
 * Desktop: a BdrMenu hanging off the navbar.
 *
 * Phone (<=600px): a BdrModal. A text field inside a `position: fixed` menu is
 * a bad place to type on iOS — the browser's "scroll the focused input into
 * view" has nothing it can move, the keyboard opening re-fires `resize` and
 * `scroll`, and the field ends up fighting for focus. Two rounds of guards in
 * BdrMenu improved it and did not fix it. A modal is the shape iOS handles
 * predictably: centred, body scroll locked, focus managed.
 *
 * The nav menu MUST be closed before this modal opens, and that is not a
 * preference — `--bdr-z-popover` is 1000001 while modals top out at 1900, so a
 * menu always paints ABOVE a modal. AdminNavbar closes it on the way in.
 *
 * WHY THE CREATE FORM IS STILL A MODAL in both shapes: ~20 fields with phone
 * rows, address autocomplete, a referral lookup and a note. A BdrMenu is a
 * floating LIST; that form inside one would be a popup the size of a page.
 * ClientFormModal is mounted HERE rather than in the navbar so creating a
 * client works from anywhere in the app.
 *
 * RECENTS ARE GLOBAL, deliberately (owner decision). `getRecentClients` reads
 * a single company-wide document capped at 20 — it is not "my recents", and
 * the header says "Recently viewed" without a possessive for that reason.
 * ─────────────────────────────────────────────────────────────────
 */

/* Nothing typed: the launcher. Module-level so a reset is the same object. */
const EMPTY_STATUS = {
  query: "",
  isNumber: false,
  settled: false,
  failed: false,
  inFlight: false,
  retry: null,
};
const EMPTY_SEARCH = { list: [], status: EMPTY_STATUS };

const ClientsNavMenu = ({
  anchorEl,
  /** dismiss THIS surface, leaving anything underneath it open */
  onClose,
  /**
   * Leaving for somewhere else — picking a client, or opening the create form.
   * Distinct from `onClose` because on the phone this sits over the nav menu,
   * and that has to come down too or it stays open over the page just
   * navigated to.
   */
  onNavigated,
  /** nesting rung; 1 when opened from the phone's nav menu (see AdminNavbar) */
  depth = 0,
}) => {
  const navigate = useNavigate();
  /*
   * 600px is the canonical phone breakpoint (WEB_STYLE_GUIDE §6), and the JS
   * and CSS values have to match exactly — a stray number creates a band where
   * neither branch is right. It decides BOTH the shape and the autofocus.
   */
  const smaller600 = useMediaQuery("(max-width:600px)");

  const [newClientOpen, setNewClientOpen] = useState(false);
  const [recents, setRecents] = useState([]);
  /*
   * Search results, drawn as THIS surface's rows.
   *
   * The first cut let ClientSearchInput render its own floating list inside
   * this floating menu: two surfaces, two keyboard models (arrows and Enter
   * drove both lists at once), and results spilling past the panel's edge.
   * One surface fixes all three — and Enter works for free on desktop, because
   * a result is just a menu row.
   */
  /*
   * The field's results AND where its search stands (see `onResults` in
   * ClientSearchInput): one object, so the two can never disagree, and so the
   * status's `retry` function is stored as a value — `setState(fn)` would call
   * it as an updater.
   */
  const [search, setSearch] = useState(EMPTY_SEARCH);
  const searchResults = search.list;
  const status = search.status;

  const [queryRecentClients] = useLazyQuery(GET_RECENT_CLIENTS, {
    fetchPolicy: "network-only",
  });

  const open = Boolean(anchorEl);

  /*
   * Fetched when this OPENS, not on mount. It lives in the navbar, so a
   * mount-time fetch would run on every page load for a list most loads never
   * show — and the list is only correct as of the moment it is opened anyway.
   */
  useEffect(() => {
    if (!open) {
      /* Status too — a reopened menu must not show the last session's
         "Search failed" above an empty field. */
      setSearch(EMPTY_SEARCH);
      return undefined;
    }
    let alive = true;
    queryRecentClients()
      .then(({ data }) => {
        if (!alive) return;
        /* Newest last on the server (the id is pushed onto the end), so this
           list is reversed to put the most recent at the top. */
        const ids = data?.getRecentClients?.clientIds || [];
        setRecents([...ids].reverse());
      })
      .catch(() => {
        if (alive) setRecents([]);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const leave = () => (onNavigated || onClose)?.();

  const openClient = (id) => {
    leave();
    navigate(`/bdr-office/clients/view-client/${id}`);
  };

  /* Stable: ClientSearchInput uses it as an effect dependency. */
  const handleResults = useCallback(
    (list, nextStatus) =>
      setSearch({ list: list || [], status: nextStatus || EMPTY_STATUS }),
    []
  );

  const searching = searchResults.length > 0;
  /* Only once THIS text's answer is in — never while it is still being
     searched, which would flash "No clients found" between keystrokes. */
  const failedNow = status.settled && status.failed;
  const emptyNow = status.settled && !status.failed && !searching;

  const nameOf = (client) =>
    `${client?.firstName || ""} ${client?.lastName || ""}`.trim() || "(no name)";

  /*
   * A NUMBER search shows the number it matched on — the same one the server
   * ranked the client by — with the matched part bold. Otherwise the email
   * line. (The preferred-Cell number used to show here even when a different
   * number had matched, so rows looked like non-matches.)
   */
  const resultMeta = (client) => {
    const match = status.isNumber
      ? pickMatchedPhone(client?.phoneNumberIds, status.query)
      : null;
    if (!match) {
      return <span className="cnm-result-meta">{buildEmailSecondary(client)}</span>;
    }
    return (
      <span className="cnm-result-meta cnm-result-meta--split">
        {client?.primaryEmail ? (
          <span className="cnm-result-email">{client.primaryEmail}</span>
        ) : null}
        <span className="cnm-result-phone">
          (<strong>{match.matched}</strong>
          {match.rest})
        </span>
      </span>
    );
  };

  const resultLabel = (client) => (
    <span className="cnm-result">
      <span className="cnm-result-name">{nameOf(client)}</span>
      {resultMeta(client)}
    </span>
  );

  const newClientRow = {
    key: "new-client",
    label: "+  New Client",
    accent: true,
    onClick: () => {
      leave();
      setNewClientOpen(true);
    },
  };

  /*
   * While a search has matches this LISTS THE MATCHES; otherwise it lists New
   * Client and recents. Deliberately one or the other: when you are searching,
   * recents are not what you are looking at, and showing both doubles the list
   * you have to get through.
   *
   * A search that FOUND NOTHING keeps New Client (a number nobody has is the
   * moment you'd create one); a search that FAILED offers Retry. Their message
   * line sits under the field, not in these rows — see `searchField`.
   *
   * ONE array, rendered two ways — as BdrMenu `items` on desktop, and as rows
   * in the modal body on a phone. Two copies of this list would drift.
   */
  const rows = searching
    ? [
        { key: "results-header", header: true, label: "Results" },
        ...searchResults.map((client) => ({
          key: `result-${client?._id}`,
          label: resultLabel(client),
          onClick: () => openClient(client?._id),
        })),
      ]
    : failedNow
    ? [
        {
          key: "retry",
          label: "Retry",
          /* keepOpen: Retry re-runs the search in place. BdrMenu hands
             onClick a DOM node and the phone rows an event — neither is an
             argument retry takes. */
          keepOpen: true,
          disabled: status.inFlight,
          onClick: () => status.retry?.(),
        },
      ]
    : emptyNow
    ? [newClientRow]
    : [
        newClientRow,
        { key: "recents-divider", divider: true },
        { key: "recents-header", header: true, label: "Recently viewed" },
        ...(recents.length
          ? recents.map((client) => ({
              key: client?._id,
              label: nameOf(client),
              onClick: () => openClient(client?._id),
            }))
          : [{ key: "recents-empty", label: "Nothing yet", disabled: true }]),
      ];

  /* Read aloud as the answer arrives — the rows alone say nothing to a screen
     reader when they change under a field that still has focus. */
  const announcement = failedNow
    ? "Search failed"
    : emptyNow
    ? "No clients found"
    : status.settled && searching
    ? `${searchResults.length} result${searchResults.length === 1 ? "" : "s"}`
    : "";

  const searchField = (
    <div className="clients-nav-menu-search">
      {/* `onResults` makes this render the FIELD ONLY — no list of its own,
          and no keyboard handling, so whatever draws the rows owns them. */}
      <ClientSearchInput
        setWidth="100%"
        onResults={handleResults}
        autoFocus={!smaller600}
      />
      {/* Here, not as menu rows: a BdrMenu row can only be a caption (dimmed,
          uppercase), a divider or a button, and an answer is none of those. */}
      {failedNow ? (
        <p className="bdr-alert bdr-alert--danger bdr-alert--inline cnm-status-alert">
          Search failed — try again
        </p>
      ) : null}
      {emptyNow ? <p className="cnm-status">No clients found</p> : null}
      <span className="visually-hidden" role="status">
        {announcement}
      </span>
    </div>
  );

  /* The phone keyboard's return key should pick the best match, the way Enter
     does on desktop. ClientSearchInput's own key handling is off in
     `onResults` mode, so the surface drawing the rows handles it. */
  const onBodyKeyDown = (event) => {
    if (event.key !== "Enter" || !searching) return;
    event.preventDefault();
    openClient(searchResults[0]?._id);
  };

  const formModal = (
    <ClientFormModal
      open={newClientOpen}
      onClose={() => setNewClientOpen(false)}
      onCreated={(client) => {
        setNewClientOpen(false);
        leave();
        navigate(`/bdr-office/clients/view-client/${client?._id}`);
      }}
    />
  );

  if (smaller600) {
    return (
      <>
        {open && (
          <BdrModal
            open
            onClose={onClose}
            title="Clients"
            size="sm"
            variant="panel"
          >
            <div className="cnm-sheet" onKeyDown={onBodyKeyDown}>
              {searchField}
              <div className="cnm-rows">
                {rows.map((row) => {
                  if (row.divider) {
                    return <div key={row.key} className="cnm-row-divider" />;
                  }
                  if (row.header) {
                    return (
                      <div key={row.key} className="cnm-row-header">
                        {row.label}
                      </div>
                    );
                  }
                  return (
                    <button
                      key={row.key}
                      type="button"
                      className={`cnm-row${row.accent ? " is-accent" : ""}`}
                      disabled={row.disabled}
                      onClick={row.onClick}
                    >
                      {row.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </BdrModal>
        )}
        {formModal}
      </>
    );
  }

  return (
    <>
      {open && (
        <BdrMenu
          anchorEl={anchorEl}
          ariaLabel="Clients"
          onClose={onClose}
          depth={depth}
          className="clients-nav-menu"
          /*
           * Wider than the 200px default because the search results carry an
           * email and a phone. This is a per-menu floor, not an app-wide
           * change — every other menu is a list of short labels.
           *
           * Set here rather than as a CSS `min-width` so the placement maths
           * knows the real width and can clamp the left edge; a CSS rule
           * widens the panel AFTER it has been positioned for a narrow one.
           */
          minWidth={420}
          /* Results are rows, so Enter should commit the top match without
             arrowing. Only while searching — pre-arming "New Client" would
             make a stray Enter open the create form. */
          autoActiveFirst={searching}
          items={rows}
        >
          {searchField}
        </BdrMenu>
      )}
      {formModal}
    </>
  );
};

export default ClientsNavMenu;
